import React, { useCallback, useMemo } from "react";
import RadioButton from "../RadioButton/RadioButton";
import { useTranslation } from "react-i18next";
import "./RadioButtonsList.scss";
import ClassNames from "classnames";
import { getDataAnalyticsProps } from "../../../utils";
import { FieldViewBlock } from "../../components";

export const CUSTOM_EMPTY = "custom_null";

export interface RadioButtonOption {
  label?: string;
  name?: string;
  value?: any;
  description?: string;
  disabled?: boolean;
  leftImg?: string;
  customOption?: any;
  withTooltip?: any;
  [key: string]: any;
}

export interface RadioButtonsListProps {
  label?: string;
  options?: RadioButtonOption[];
  disabled?: boolean;
  required?: boolean;
  noMargin?: boolean;
  value?: any;
  onChange?: (value: any) => void;
  hideLabelHelper?: boolean;
  tabStyle?: boolean;
  pillStyle?: boolean;
  buttonStyle?: boolean;
  setFirstAsDefault?: boolean;
  "data-testid"?: string;
  direction?: "row" | "column";
  labelTop?: boolean;
  itemLabelWidth?: string;
  alignLeft?: boolean;
  className?: string;
  optionWidth?: string | number;
  viewMode?: boolean;
  [key: string]: any;
}

const RadioButtonsList: React.FC<RadioButtonsListProps> = ({
  label,
  options,
  disabled,
  required,
  noMargin,
  value,
  onChange,
  hideLabelHelper,
  tabStyle,
  pillStyle,
  buttonStyle,
  setFirstAsDefault = true,
  "data-testid": dataTestId,
  direction = "row",
  labelTop,
  itemLabelWidth,
  alignLeft,
  className = "",
  optionWidth,
  viewMode = false,
  ...props
}) => {
  const { t } = useTranslation();

  const handleChange = (cbValue: any) => {
    onChange?.(cbValue);
  };

  const classNameWrapper = ClassNames(
    "input-wrap radio-button-list-wrapper",
    direction && `has-direction-${direction}`,
    className && className,
    {
      "hide-label-helper": hideLabelHelper,
      "has-no-margin": noMargin,
      "has-label-top": labelTop,
      "has-tab-style": tabStyle,
      "has-pill-style": pillStyle,
      "has-button-style": buttonStyle,
      "has-align-left": alignLeft,
    }
  );

  const isChecked = useCallback(
    (option: RadioButtonOption) => {
      if (!value && value !== "" && setFirstAsDefault) {
        onChange?.(options?.[0]?.value);
        return option.value === options?.[0]?.value;
        // clicked on "custom" input or selected value is not a part of the list (= custom option should be checked)
      } else if (
        !options?.filter((option) => option.value === value)?.length ||
        value === CUSTOM_EMPTY
      ) {
        return option.value === "custom";
      } else {
        return option.value === value;
      }
    },
    [options, value]
  );

  const selectedOption = useMemo(() => {
    return options?.find((option) => option?.value === value);
  }, [options, value]);

  return (
    <div className={classNameWrapper} data-testid={`${dataTestId}-radio`}>
      {!viewMode ? (
        <>
          {!!label && (
            <label className={`input-label`}>
              {t(label)}
              {!required && !hideLabelHelper && (
                <span className={"label-helper"}>Optional</span>
              )}
            </label>
          )}
          <ul className="radio-button-list-options-wrap">
            {options?.map((option, index) => (
              <li
                key={`radio-item-${index}`}
                className={`radio-button-list-option`}
                style={optionWidth ? { width: optionWidth } : undefined}
              >
                <RadioButton
                  data-testid={option.value?.toLowerCase()}
                  disabled={option.disabled || disabled}
                  label={option.label}
                  description={option.description}
                  itemLabelWidth={itemLabelWidth}
                  checked={isChecked(option)}
                  value={option.value}
                  onChange={handleChange}
                  name={(option.name as string) + index}
                  leftImg={option.leftImg}
                  customOption={option.customOption}
                  selectedValue={value}
                  withTooltip={option.withTooltip}
                  tabStyle={tabStyle}
                  pillStyle={pillStyle}
                  buttonStyle={buttonStyle}
                  {...getDataAnalyticsProps({ ...props, ...option })}
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <FieldViewBlock label={label} className="radio-button-list-view-mode">
          <div
            className="radio-button-list-view-mode-value"
            data-testid="radio-button-list-view-mode-value"
          >
            {!!selectedOption?.leftImg && (
              <img src={selectedOption?.leftImg} alt={selectedOption.name} />
            )}
            {selectedOption?.label}
          </div>
        </FieldViewBlock>
      )}
    </div>
  );
};

export default RadioButtonsList;
